import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Badge, Button, Spinner, Table,
} from 'react-bootstrap';
import BillingoService from '../../../Services/BillingoService';
import BillingoFailureNote from '../../../Components/Billingo/BillingoFailureNote';
import ExpandableBookingsCell from '../../../Components/Billingo/ExpandableBookingsCell';
import StaleApiNotice from '../../../Components/Billingo/StaleApiNotice';
import useDocumentIssuer from '../../../Components/Billingo/useDocumentIssuer';
import ListPager from '../../../Components/ListPager';
import { docTypeLabel, formatAmount } from '../../../Utils/billingoDocuments';

// What was being attempted, in the words the desk uses.
const OPERATION_LABELS = {
  create: 'Issue',
  create_from_proforma: 'Számla from Díjbekérő',
  send: 'Email to customer',
  cancel: 'Storno',
  archive: 'Archive',
  register_payment: 'Mark paid in Billingo',
};

// Only these bring a document into existence, so only these can leave an
// order without one — and only these are retried by issuing it again. A
// failed send or storno acts on a document that exists; it is retried from
// that document's own actions.
const ISSUING = ['create', 'create_from_proforma'];

/**
 * Failed documents — every Billingo document operation that did not complete,
 * with Billingo's own reason (GET /billingo?action=failed-documents). Before
 * this the refusals were readable only in CloudWatch: one order was refused
 * nineteen times and nobody at the desk could see why.
 *
 * A refused issue whose document still does not exist offers Retry, which
 * opens the normal flow through useDocumentIssuer — the order's documents
 * loaded, the right next document recommended. The reason sits beside it, to
 * be read before clicking again.
 */
function FailedDocuments({ refreshKey = 0, onSummaryChange = () => {}, onChanged = () => {} }) {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const next = await BillingoService.listFailedDocuments({ page, limit });
      setResult(next);
      if (!next.stale) onSummaryChange(next.summary);
    } catch (err) {
      // An empty list would read as "nothing failed" — the one wrong answer.
      setResult(null);
      setLoadError(err.response?.data?.message || err.message || 'Could not load the failed documents.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [page, limit, refreshKey]);

  useEffect(() => { load(); }, [load]);

  const reloadAll = () => { load(); onChanged(); };
  const {
    issue, preparing, error, modal,
  } = useDocumentIssuer({ onIssued: reloadAll });

  const rows = result?.rows || [];
  const summary = result?.summary;

  return (
    <div>
      <div className="mb-3">
        <h6 className="mb-1">Billingo operations that did not complete</h6>
        {summary && summary.total > 0 && (
          <div className="text-muted small">
            {`${summary.total} failure${summary.total > 1 ? 's' : ''} across ${summary.order_groups} order group${summary.order_groups > 1 ? 's' : ''}`}
            {' · '}
            <span className={summary.groups_still_missing_document ? 'text-danger fw-medium' : ''}>
              {`${summary.groups_still_missing_document} still without the document`}
            </span>
          </div>
        )}
      </div>

      {loadError && (
        <Alert variant="danger" className="py-2 small d-flex justify-content-between align-items-center">
          <span>{loadError}</span>
          <Button size="sm" variant="outline-danger" onClick={load}>Try again</Button>
        </Alert>
      )}
      {error && <Alert variant="danger" className="py-2 small">{error}</Alert>}

      <StaleApiNotice show={!!result?.stale} detail="the failed-documents list" />

      {loading && (
        <div className="text-muted small mb-2">
          <Spinner size="sm" animation="border" className="me-2" />
          Loading…
        </div>
      )}

      {!loading && result && !result.stale && rows.length === 0 && (
        <div className="text-center text-muted py-5">
          <i className="fa fa-circle-check fa-2x mb-2 d-block text-success opacity-75" />
          No Billingo document operation has failed.
        </div>
      )}

      {rows.length > 0 && (
        <Table responsive hover className="align-middle responsive-card-table">
          <thead className="table-light">
            <tr>
              <th>Order(s)</th>
              <th>Attempted</th>
              <th>Why it failed</th>
              <th className="text-end" aria-label="actions" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const issuing = ISSUING.includes(row.operation);
              return (
                <tr key={row.id}>
                  <td data-label="Order(s)" style={{ maxWidth: 220 }}>
                    <ExpandableBookingsCell fallbackRefs={row.order_references} />
                  </td>
                  <td data-label="Attempted" className="small">
                    <div className="fw-medium">
                      {[OPERATION_LABELS[row.operation] || row.operation,
                        row.document_type && docTypeLabel(row.document_type)].filter(Boolean).join(' · ')}
                    </div>
                    {typeof row.total === 'number' && (
                      <div className="text-muted">{formatAmount(row.total, row.currency)}</div>
                    )}
                  </td>
                  <td data-label="Why it failed" style={{ maxWidth: 420 }}>
                    <BillingoFailureNote failure={row} />
                  </td>
                  <td className="text-end text-nowrap">
                    {issuing && row.document_present && (
                      <Badge bg="success-subtle" text="success-emphasis" className="fw-normal">
                        Issued since
                      </Badge>
                    )}
                    {issuing && !row.document_present && row.booking_ids.length > 0 && (
                      <Button
                        size="sm"
                        variant="outline-primary"
                        onClick={() => issue({ group_key: row.id, booking_ids: row.booking_ids })}
                        disabled={preparing === row.id}
                        aria-label={`Retry ${(row.order_references || [])[0] || ''}`.trim()}
                      >
                        {preparing === row.id ? 'Opening…' : 'Retry'}
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}

      {result?.pagination && (
        <ListPager
          pagination={result.pagination}
          onPageChange={setPage}
          onLimitChange={(n) => { setLimit(n); setPage(1); }}
        />
      )}

      {modal}
    </div>
  );
}

export default FailedDocuments;
